import React, { forwardRef, HTMLProps, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Transition } from 'react-transition-group';
import { usePopper } from 'react-popper';
import { Placement, Rect } from '@popperjs/core';
import './style.scss';

type OffsetsFunction = (popper: Rect, reference: Rect, placement: Placement) => [number?, number?];
export type RegisterPopperMethods = Pick<ReturnType<typeof usePopper>, 'forceUpdate' | 'update'>;

export interface IPopover extends HTMLProps<HTMLDivElement> {
    targetElement: HTMLElement;
    className?: string,
    placement?: Placement,
    registerPopperMethods?: (popperMethods: RegisterPopperMethods) => void;
    offset?: OffsetsFunction | [number?, number?],
    open: boolean,
    showTimeout?: number,
    hideTimeout?: number;
    white?: boolean
}

export const Popover = forwardRef<HTMLDivElement, IPopover>((props, ref) => {
    const {
        targetElement = null,
        className = '',
        children,
        offset,
        placement,
        open = false,
        showTimeout = 0,
        hideTimeout = 0,
        white = false
    } = props;

    const [ popperElement, setPopperElement ] = useState(null);
    const [ isOpen, setIsOpen ] = useState(false);
    const [ isMouseOver, setIsMouseOver ] = useState(false);
    const [ showTimerId, setShowTimerId ] = useState(null);
    const [ hideTimerId, setHideTimerId ] = useState(null);
    const [ arrowElement, setArrowElement ] = useState(null);

    const clearTimeouts = () => {
        clearTimeout(showTimerId);
        clearTimeout(hideTimerId);
    };

    useEffect(() => {
        let timerId;
        clearTimeouts();
        if (open) {
            timerId = setTimeout(() => {
                setIsOpen(open);
            }, showTimeout);
            setShowTimerId(timerId);
        } else {
            timerId = setTimeout(() => {
                if (!isMouseOver) {
                    setIsOpen(open);
                }
            }, hideTimeout);
            setHideTimerId(timerId);
        }
    }, [ open ]);

    const modifiers = [];
    if (offset) {
        modifiers.push({
            name: 'offset',
            options: {
                offset
            }
        });
    }

    if (white) {
        modifiers.push({ name: 'arrow', options: { element: arrowElement } });
    }

    const handleMouseEnter = () => {
        setIsMouseOver(true);
        clearTimeout(hideTimerId);
    };

    const handleMouseLeave = () => {
        clearTimeouts();
        setIsMouseOver(false);
        let timerId = setTimeout(() => {
            setIsOpen(false);
        }, hideTimeout);
        setHideTimerId(timerId);
    };

    const { styles, attributes, forceUpdate, update } = usePopper(targetElement, popperElement, {
        placement,
        modifiers: modifiers
    });

    props.registerPopperMethods?.({
        forceUpdate,
        update
    });

    const classes = clsx('oms-popover', className, white && 'white');

    return (
        <Transition in={isOpen} timeout={500} mountOnEnter unmountOnExit>
            {state => (
                <div
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    className={clsx(classes, state)}
                    ref={setPopperElement}
                    style={styles.popper}
                    {...attributes.popper}
                >
                    {white && (
                        <div ref={setArrowElement} style={styles.arrow} className="oms-popover-arrow" />
                    )}
                    {children}
                </div>
            )}
        </Transition>
    );
});
